import { createHash, randomBytes, timingSafeEqual } from 'node:crypto';
import { Plan } from '../billing/entitlements.js';

/** Plans that can be licensed via a key. */
export type LicensablePlan = Plan.PRO | Plan.TEAM;

const PLAN_CODES: Record<string, LicensablePlan> = {
  pro: Plan.PRO,
  team: Plan.TEAM,
};

/** BF-<plan>-<random32hex>-<checksum4hex> */
const KEY_REGEX = /^BF-(pro|team)-([0-9a-f]{32})-([0-9a-f]{4})$/i;

/** First 4 hex chars of SHA-256 of the payload. */
export function computeChecksum(payload: string): string {
  return createHash('sha256').update(payload).digest('hex').slice(0, 4);
}

/** Generate a new license key for the given plan. */
export function generateKey(plan: LicensablePlan): string {
  const random = randomBytes(16).toString('hex'); // 32 hex chars
  const planCode = plan === Plan.PRO ? 'pro' : 'team';
  const payload = `BF-${planCode}-${random}`;
  const checksum = computeChecksum(payload);
  return `${payload}-${checksum}`;
}

export interface KeyValidationResult {
  valid: boolean;
  plan: LicensablePlan | null;
}

/** Validate key format and checksum. No network call. */
export function validateKeyFormat(key: string): KeyValidationResult {
  const match = KEY_REGEX.exec(key);
  if (!match) return { valid: false, plan: null };

  const [, planCode, random, givenChecksum] = match;
  const payload = `BF-${planCode.toLowerCase()}-${random.toLowerCase()}`;
  const expectedChecksum = computeChecksum(payload);

  const given = Buffer.from(givenChecksum.toLowerCase(), 'utf-8');
  const expected = Buffer.from(expectedChecksum, 'utf-8');
  if (!timingSafeEqual(given, expected)) {
    return { valid: false, plan: null };
  }

  const plan = PLAN_CODES[planCode.toLowerCase()];
  if (!plan) return { valid: false, plan: null };

  return { valid: true, plan };
}
